import type { SalesOrderPrintTemplate } from '@/lib/services/snapdragon/snapdragon.types'
import type { SuperTicketLayoutInput } from '@/lib/services/super-ticket-settings/super-ticket-settings.types'
import type { SalesOrderLocalPrintBundle } from './sales-orders.types'
import axios from '@/lib/axios'
import { buildAdminRequestConfig, type OrganizationId } from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type {
  RefundQuote,
  RefundRequestActionResult,
  RefundRequestStatusSnapshot,
} from '@/lib/services/refunds/refunds.types'
import type {
  CreateSalesOrderArrangementPhotoInput,
  DuplicateSalesOrderCartResult,
  QuoteSalesOrderRefundInput,
  RefundExtraPaymentInput,
  RefundFullSalesOrderInput,
  RefundPartialSalesOrderInput,
  ReplaceSalesOrderArrangementPhotoGalleryInput,
  SalesOrder,
  SalesOrderAnalytics,
  SalesOrderAnalyticsFilters,
  SalesOrderArrangementPhoto,
  SalesOrderArrangementPhotoSendMode,
  SalesOrderAssignee,
  SalesOrderCalendarDay,
  SalesOrderCancellationResult,
  SalesOrderCancellationStatus,
  SalesOrderIdInput,
  SalesOrderIndexFilters,
  SalesOrderIndexResponse,
  SalesOrderMessageResult,
  SalesOrderPrintOptions,
  SendCustomerCopyResult,
  SoftDeleteSalesOrderInput,
  SoftDeleteSalesOrderResult,
  UpdateSalesOrderArrangementPhotoInput,
  UpdateSalesOrderInput,
  UpdateSalesOrderPurchaserInput,
} from './sales-orders.types'

export interface SalesOrderOrganizationScope {
  organizationId: OrganizationId
}

/** Query functions read only the key (and the abort signal when the caller passes one). */
type SalesOrderQueryContext<TKey> = Pick<QueryFunctionContext<[TKey]>, 'queryKey'> &
  Partial<Pick<QueryFunctionContext<[TKey]>, 'signal'>>

export type SalesOrderIndexQueryKeyEntries = SalesOrderOrganizationScope & {
  filters?: SalesOrderIndexFilters | undefined
}

/** Scope for calls that act on one order by `id`. */
interface SalesOrderScope extends SalesOrderOrganizationScope {
  id: SalesOrderIdInput
}

type OptionalSalesOrderId = SalesOrderOrganizationScope & { salesOrderId?: SalesOrderIdInput }

type ArrangementPhotoSendModeField = SalesOrderArrangementPhotoSendMode | null | undefined

export async function getSalesOrders({
  queryKey: [{ organizationId, filters = {} }],
  signal,
}: SalesOrderQueryContext<SalesOrderIndexQueryKeyEntries>) {
  return (
    (await axios.get<SalesOrderIndexResponse>(
      '/sales-orders',
      buildAdminRequestConfig({
        organizationId,
        params: { ...filters },
        ...(signal ? { signal } : {}),
      })
    )) || []
  )
}

export async function getSalesOrderAssignees({
  queryKey: [{ organizationId }],
  signal,
}: SalesOrderQueryContext<SalesOrderOrganizationScope>) {
  return (
    (await axios.get<SalesOrderAssignee[]>(
      '/sales-orders/assignees',
      buildAdminRequestConfig({
        organizationId,
        ...(signal ? { signal } : {}),
      })
    )) || []
  )
}

export async function getSalesOrderCalendarSummary({
  queryKey: [{ organizationId, filters = {} }],
  signal,
}: SalesOrderQueryContext<SalesOrderIndexQueryKeyEntries>) {
  return (
    (await axios.get<SalesOrderCalendarDay[]>(
      '/sales-orders/calendar-summary',
      buildAdminRequestConfig({
        organizationId,
        params: { ...filters },
        ...(signal ? { signal } : {}),
      })
    )) || []
  )
}

export async function getInfiniteSalesOrders({
  pageParam = 1,
  queryKey: [{ organizationId, filters = {}, pageSize = 20 }],
  signal,
}: SalesOrderQueryContext<SalesOrderIndexQueryKeyEntries & { pageSize?: number | undefined }> & {
  pageParam?: number
}) {
  return getSalesOrders({
    queryKey: [
      {
        organizationId,
        filters: {
          ...filters,
          page: pageParam,
          page_size: pageSize,
        },
      },
    ],
    signal,
  })
}

export async function getSalesOrderAnalytics({
  queryKey: [{ organizationId, filters = {} }],
}: SalesOrderQueryContext<
  SalesOrderOrganizationScope & { filters?: SalesOrderAnalyticsFilters | undefined }
>) {
  return (
    (await axios.get<SalesOrderAnalytics>(
      '/sales-orders/analytics',
      buildAdminRequestConfig({ organizationId, params: { ...filters } })
    )) || []
  )
}

export async function getSalesOrder({
  queryKey: [{ organizationId, salesOrderId }],
}: SalesOrderQueryContext<SalesOrderOrganizationScope & { salesOrderId?: SalesOrderIdInput }>) {
  const response = await axios.get<SalesOrder>(
    `/sales-orders/${String(salesOrderId)}`,
    buildAdminRequestConfig({ organizationId })
  )
  return response || {}
}

/** Resolves to the rendered print page's HTML. */
export async function printSalesOrder({
  queryKey: [{ organizationId, paperHasPreprintedBranding, salesOrderId, template, layout }],
}: SalesOrderQueryContext<
  SalesOrderOrganizationScope & SalesOrderPrintOptions & { salesOrderId?: SalesOrderIdInput }
>) {
  const response = await axios.get<string>(
    `/sales-orders/print/${String(salesOrderId)}`,
    buildAdminRequestConfig({
      organizationId,
      ...(template || layout || typeof paperHasPreprintedBranding === 'boolean'
        ? {
            params: {
              ...(template ? { template } : {}),
              ...(layout ? { layout: JSON.stringify(layout) } : {}),
              ...(typeof paperHasPreprintedBranding === 'boolean'
                ? { paper_has_preprinted_branding: paperHasPreprintedBranding ? 1 : 0 }
                : {}),
            },
          }
        : {}),
    })
  )
  return response || ''
}

export async function updateSalesOrder({
  organizationId,
  id,
  ...values
}: SalesOrderScope & UpdateSalesOrderInput) {
  return axios.put<SalesOrder>(
    `/sales-orders/${id}`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateSalesOrderPurchaser({
  organizationId,
  id,
  ...values
}: SalesOrderScope & UpdateSalesOrderPurchaserInput) {
  return axios.put<SalesOrder>(
    `/sales-orders/${id}/purchaser`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function quoteSalesOrderRefund({
  organizationId,
  id,
  mode,
  lineItems,
}: SalesOrderScope & QuoteSalesOrderRefundInput) {
  return axios.post<RefundQuote>(
    `/sales-orders/${id}/refund-quote`,
    {
      mode,
      ...(Array.isArray(lineItems) ? { lineItems } : {}),
    },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function refundPartialSalesOrder({
  organizationId,
  id,
  amount,
  lineItems,
  restockLineItems,
  quoteMode,
  returnFulfillmentStatus,
  idempotencyKey,
  cashHandoffConfirmed,
  cashDrawerSessionId,
  sendCustomerEmail,
  reason,
  reasonNote,
}: SalesOrderScope & RefundPartialSalesOrderInput) {
  return axios.post<RefundRequestActionResult>(
    `/sales-orders/${id}/partial-refund`,
    {
      amount,
      lineItems,
      ...(restockLineItems?.length ? { restockLineItems } : {}),
      ...(quoteMode ? { quote_mode: quoteMode } : {}),
      returnFulfillmentStatus,
      idempotency_key: idempotencyKey,
      ...(cashHandoffConfirmed ? { cash_handoff_confirmed: true } : {}),
      ...(cashDrawerSessionId ? { cash_drawer_session_id: cashDrawerSessionId } : {}),
      ...(sendCustomerEmail !== undefined ? { send_customer_email: sendCustomerEmail } : {}),
      ...(reason ? { reason } : {}),
      ...(reasonNote ? { reason_note: reasonNote } : {}),
    },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function refundFullSalesOrder({
  organizationId,
  id,
  quoteMode,
  expectedTotal,
  restockLineItems,
  returnFulfillmentStatus,
  idempotencyKey,
  cashHandoffConfirmed,
  cashDrawerSessionId,
  sendCustomerEmail,
  reason,
  reasonNote,
}: SalesOrderScope & RefundFullSalesOrderInput) {
  return axios.post<RefundRequestActionResult>(
    `/sales-orders/${id}/full-refund`,
    {
      ...(quoteMode ? { quote_mode: quoteMode } : {}),
      ...(expectedTotal != null ? { expected_total: expectedTotal } : {}),
      ...(restockLineItems?.length ? { restockLineItems } : {}),
      returnFulfillmentStatus,
      idempotency_key: idempotencyKey,
      ...(cashHandoffConfirmed ? { cash_handoff_confirmed: true } : {}),
      ...(cashDrawerSessionId ? { cash_drawer_session_id: cashDrawerSessionId } : {}),
      ...(sendCustomerEmail !== undefined ? { send_customer_email: sendCustomerEmail } : {}),
      ...(reason ? { reason } : {}),
      ...(reasonNote ? { reason_note: reasonNote } : {}),
    },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function refundExtraPayment({
  organizationId,
  id,
  expectedTotal,
  idempotencyKey,
  cashHandoffConfirmed,
  cashDrawerSessionId,
  sendCustomerEmail,
}: SalesOrderScope & RefundExtraPaymentInput) {
  return axios.post<RefundRequestActionResult>(
    `/sales-orders/${id}/extra-payment-refund`,
    {
      expected_total: expectedTotal,
      idempotency_key: idempotencyKey,
      send_customer_email: sendCustomerEmail,
      ...(cashHandoffConfirmed ? { cash_handoff_confirmed: true } : {}),
      ...(cashDrawerSessionId ? { cash_drawer_session_id: cashDrawerSessionId } : {}),
    },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function getRefundRequestStatus({
  organizationId,
  refundRequestId,
}: SalesOrderOrganizationScope & { refundRequestId?: SalesOrderIdInput | null }) {
  return axios.get<RefundRequestStatusSnapshot>(
    `/refund-requests/${String(refundRequestId)}/status`,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function confirmCashRefundRequestPayment({
  organizationId,
  refundRequestId,
  paymentId,
  cashDrawerSessionId,
}: SalesOrderOrganizationScope & {
  refundRequestId: SalesOrderIdInput
  paymentId: SalesOrderIdInput
  cashDrawerSessionId?: SalesOrderIdInput | null
}) {
  return axios.post<RefundRequestActionResult>(
    `/refund-requests/${refundRequestId}/payments/${paymentId}/confirm-cash`,
    cashDrawerSessionId ? { cash_drawer_session_id: cashDrawerSessionId } : {},
    buildAdminRequestConfig({ organizationId })
  )
}

/** Resolves to an empty object when the order has no cancellation. */
export async function getCancellationStatus({
  organizationId,
  salesOrderId,
}: SalesOrderOrganizationScope & { salesOrderId: SalesOrderIdInput }) {
  return axios.get<SalesOrderCancellationStatus | Record<string, never>>(
    `/sales-orders/${salesOrderId}/cancellation`,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function retryCancellationRefund({
  organizationId,
  salesOrderId,
  refundAttempt,
  cashDrawerSessionId,
}: SalesOrderOrganizationScope & {
  salesOrderId: SalesOrderIdInput
  refundAttempt: number
  cashDrawerSessionId?: SalesOrderIdInput | null
}) {
  return axios.post<SalesOrderCancellationResult>(
    `/sales-orders/${salesOrderId}/retry-cancellation-refund`,
    {
      refund_attempt: refundAttempt,
      ...(cashDrawerSessionId ? { cash_drawer_session_id: cashDrawerSessionId } : {}),
    },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function softDeleteSalesOrder({
  organizationId,
  salesOrderId,
  reason,
  reasonNote,
  cashDrawerSessionId,
  sendCustomerEmail,
}: SalesOrderOrganizationScope & SoftDeleteSalesOrderInput) {
  return axios.delete<SoftDeleteSalesOrderResult>(
    `/sales-orders/${salesOrderId}`,
    buildAdminRequestConfig({
      organizationId,
      data: {
        ...(sendCustomerEmail !== undefined ? { send_customer_email: sendCustomerEmail } : {}),
        ...(reason ? { reason } : {}),
        ...(reasonNote ? { reason_note: reasonNote } : {}),
        ...(cashDrawerSessionId ? { cash_drawer_session_id: cashDrawerSessionId } : {}),
      },
    })
  )
}

// Where an id can be missing (query keys, `{}` defaults, unresolved refund ids), `String()` spells
// out the template literal's own coercion of it.

export async function sendCustomerCopy({
  organizationId,
  salesOrderId,
  email,
}: OptionalSalesOrderId & { email?: string | null }) {
  return axios.post<SendCustomerCopyResult>(
    `/sales-orders/${String(salesOrderId)}/send-customer-copy`,
    email ? { email } : {},
    buildAdminRequestConfig({ organizationId })
  )
}

/** Resolves to one photo, or a list when several were saved. */
export async function createSalesOrderArrangementPhoto({
  organizationId,
  salesOrderId,
  mediaIds,
  media_ids,
  mediaId,
  media_id,
  sendMode,
  send_mode,
  scheduledSendDate,
  scheduled_send_date,
}: SalesOrderOrganizationScope & Partial<CreateSalesOrderArrangementPhotoInput>) {
  const resolvedMediaIds = mediaIds ?? media_ids
  const payload: {
    send_mode: ArrangementPhotoSendModeField
    media_ids?: number[]
    media_id?: number | null | undefined
    scheduled_send_date?: string
  } = {
    send_mode: sendMode ?? send_mode,
  }
  const resolvedScheduledSendDate = scheduledSendDate ?? scheduled_send_date

  if (Array.isArray(resolvedMediaIds)) {
    payload.media_ids = resolvedMediaIds
  } else {
    payload.media_id = mediaId ?? media_id
  }

  if (resolvedScheduledSendDate) {
    payload.scheduled_send_date = resolvedScheduledSendDate
  }

  return axios.post<SalesOrderArrangementPhoto | SalesOrderArrangementPhoto[]>(
    `/sales-orders/${String(salesOrderId)}/arrangement-photos`,
    payload,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateSalesOrderArrangementPhoto({
  organizationId,
  salesOrderId,
  photoId,
  mediaId,
  media_id,
  sendMode,
  send_mode,
  scheduledSendDate,
  scheduled_send_date,
}: SalesOrderOrganizationScope & Partial<UpdateSalesOrderArrangementPhotoInput>) {
  const payload: {
    media_id: number | null | undefined
    send_mode: ArrangementPhotoSendModeField
    scheduled_send_date?: string
  } = {
    media_id: mediaId ?? media_id,
    send_mode: sendMode ?? send_mode,
  }
  const resolvedScheduledSendDate = scheduledSendDate ?? scheduled_send_date

  if (resolvedScheduledSendDate) {
    payload.scheduled_send_date = resolvedScheduledSendDate
  }

  return axios.patch<SalesOrderArrangementPhoto>(
    `/sales-orders/${String(salesOrderId)}/arrangement-photos/${String(photoId)}`,
    payload,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function replaceSalesOrderArrangementPhotoGallery({
  organizationId,
  salesOrderId,
  mediaIds,
  media_ids,
  sendMode,
  send_mode,
  scheduledSendDate,
  scheduled_send_date,
}: SalesOrderOrganizationScope & Partial<ReplaceSalesOrderArrangementPhotoGalleryInput>) {
  const payload: {
    media_ids: number[] | null | undefined
    send_mode?: ArrangementPhotoSendModeField
    scheduled_send_date?: string
  } = {
    media_ids: mediaIds ?? media_ids,
    ...(sendMode || send_mode ? { send_mode: sendMode ?? send_mode } : {}),
  }
  const resolvedScheduledSendDate = scheduledSendDate ?? scheduled_send_date

  if (resolvedScheduledSendDate) {
    payload.scheduled_send_date = resolvedScheduledSendDate
  }

  return axios.put<SalesOrderArrangementPhoto[]>(
    `/sales-orders/${String(salesOrderId)}/arrangement-photos`,
    payload,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function deleteSalesOrderArrangementPhoto({
  organizationId,
  salesOrderId,
  photoId,
}: OptionalSalesOrderId & { photoId?: SalesOrderIdInput }) {
  return axios.delete<SalesOrderMessageResult>(
    `/sales-orders/${String(salesOrderId)}/arrangement-photos/${String(photoId)}`,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function deleteSalesOrderArrangementPhotoGallery({
  organizationId,
  salesOrderId,
}: OptionalSalesOrderId) {
  return axios.delete<SalesOrderMessageResult>(
    `/sales-orders/${String(salesOrderId)}/arrangement-photos`,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function duplicateSalesOrderToCart({
  organizationId,
  salesOrderId,
}: OptionalSalesOrderId) {
  return axios.post<DuplicateSalesOrderCartResult>(
    `/sales-orders/${String(salesOrderId)}/duplicate-cart`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function getLocalPrintBundle({
  organizationId,
  salesOrderId,
  template,
  paperHasPreprintedBranding,
  layout,
}: {
  organizationId: OrganizationId
  salesOrderId: SalesOrderIdInput
  template: SalesOrderPrintTemplate
  paperHasPreprintedBranding?: boolean
  layout?: SuperTicketLayoutInput
}): Promise<SalesOrderLocalPrintBundle> {
  return axios.get<SalesOrderLocalPrintBundle>(
    `/sales-orders/${salesOrderId}/local-print-bundle`,
    buildAdminRequestConfig({
      organizationId,
      params: {
        template,
        ...(layout ? { layout: JSON.stringify(layout) } : {}),
        ...(typeof paperHasPreprintedBranding === 'boolean'
          ? { paper_has_preprinted_branding: paperHasPreprintedBranding ? 1 : 0 }
          : {}),
      },
    })
  )
}
